import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, UploadSimple, TextB, TextItalic, TextUnderline } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { track } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_CHARS = 5000;
const QUICK_EMOJI = ["😅", "😂", "🥲", "💪", "🦒", "👖", "🏀", "✈️"];

// The editor is an UNCONTROLLED contenteditable: React never re-renders its
// subtree, so the caret position survives typing. We only read innerHTML at
// submit time. The server sanitizes to a b/i/u-family allowlist and strips
// links, so whatever ends up here is advisory UX, not the trust boundary.
// Passing `story` switches to admin edit mode: text only, saved with PUT.
const StoryComposer = ({ open, onClose, onSuccess, story = null }) => {
  const editing = !!story;
  useBodyScrollLock(open);
  const editorRef = useRef(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && !editing) track("form_start", { form_name: "story", form_id: "story_composer" });
  }, [open, editing]);

  // Seed the uncontrolled editor once per open with the story being edited.
  useEffect(() => {
    if (!open) return;
    const el = editorRef.current;
    if (!el) return;
    el.innerHTML = story?.content_html || "";
    setCharCount(el.innerText.trim().length);
  }, [open, story]);

  if (!open) return null;

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const handlePaste = (e) => {
    // Paste as plain text — kills pasted links and foreign markup at the source.
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
  };

  // Closing with unsaved text is a different signal from closing an empty
  // composer — the first is a lost story, the second is idle curiosity.
  const handleClose = () => {
    if (editing) return onClose();
    const text = (editorRef.current?.innerText || "").trim();
    track("form_abandon", {
      form_name: "story",
      form_id: "story_composer",
      had_content: text.length > 0,
      char_count: text.length,
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = (editorRef.current?.innerText || "").trim();
    if (!text) {
      track("form_error", { form_name: "story", reason: "empty" });
      toast.error("Please write your story first");
      return;
    }
    if (text.length > MAX_CHARS) {
      track("form_error", { form_name: "story", reason: "too_long", char_count: text.length });
      toast.error(`Stories are limited to ${MAX_CHARS} characters`);
      return;
    }
    if (editing) {
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("content_html", editorRef.current.innerHTML);
        const res = await axios.put(`${API}/stories/${story.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        track("story_edit", { story_id: story.id, char_count: text.length });
        toast.success("Story updated");
        onClose();
        if (onSuccess) onSuccess(res.data);
      } catch (err) {
        console.error("Story edit failed:", err);
        toast.error(err.response?.data?.detail || "Could not update the story");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    track("form_submit", {
      form_name: "story",
      form_id: "story_composer",
      char_count: text.length,
      has_image: !!image,
    });
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("content_html", editorRef.current.innerHTML);
      if (image) fd.append("image", image);
      await axios.post(`${API}/stories`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      track("share", {
        method: "story_composer",
        content_type: "story",
        char_count: text.length,
        has_image: !!image,
      });
      toast.success("Your story is live!");
      clearImage();
      if (editorRef.current) editorRef.current.innerHTML = "";
      setCharCount(0);
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Story submit failed:", err);
      track("share_failed", {
        method: "story_composer",
        content_type: "story",
        error_status: err?.response?.status,
      });
      toast.error(err.response?.data?.detail || "Could not share your story");
    } finally {
      setSubmitting(false);
    }
  };

  const toolbarButton = (Icon, command, label) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep the editor selection
      onClick={() => exec(command)}
      className="p-2 rounded hover:bg-[#F0EFEB] text-[#2D302A] transition-colors"
      title={label}
      aria-label={label}
      data-testid={`story-format-${command}`}
    >
      <Icon size={18} weight="bold" />
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center"
      data-testid="story-composer-overlay"
    >
      <div
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="story-composer-dialog"
      >
        <div className="flex justify-between items-start mb-6">
          <h2
            className="text-3xl tracking-tighter"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            {editing ? "Edit Story" : "Your Story"}
          </h2>
          <button
            onClick={handleClose}
            className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
            data-testid="story-close-button"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {!editing && (
          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              A picture (optional)
            </label>
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Story preview"
                  className="w-full max-h-64 object-cover rounded-md border border-[#E4E1D9]"
                />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 text-[#2D302A] hover:bg-white"
                  aria-label="Remove image"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-3 border border-dashed border-[#C9C5BA] rounded-md px-4 py-5 cursor-pointer hover:bg-[#FAF9F5] transition-colors">
                <UploadSimple size={20} className="text-[#6B6A68]" />
                <span className="text-sm text-[#6B6A68]">Add a photo</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="story-image-input"
                />
              </label>
            )}
          </div>
          )}

          <div>
            <label
              className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              {editing ? `Editing ${story.user_name || "this"}'s story` : "What's your story of being tall?"}
            </label>
            <div className="border border-[#E4E1D9] rounded-md focus-within:border-[#2D302A] transition-colors">
              <div className="flex items-center gap-1 border-b border-[#E4E1D9] px-2 py-1">
                {toolbarButton(TextB, "bold", "Bold")}
                {toolbarButton(TextItalic, "italic", "Italic")}
                {toolbarButton(TextUnderline, "underline", "Underline")}
                <span className="w-px h-5 bg-[#E4E1D9] mx-1" />
                <div className="flex gap-0.5 overflow-x-auto no-scrollbar">
                  {QUICK_EMOJI.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => exec("insertText", emoji)}
                      className="px-1.5 py-1 rounded hover:bg-[#F0EFEB] text-base"
                      aria-label={`Insert ${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Growing up tall meant..."
                onPaste={handlePaste}
                onInput={() => setCharCount(editorRef.current?.innerText.trim().length || 0)}
                className="story-editor min-h-40 max-h-72 overflow-y-auto px-4 py-3 text-[15px] leading-relaxed text-[#2D302A] outline-none"
                data-testid="story-editor"
              />
            </div>
            <p
              className={`mt-1 text-right text-[11px] ${charCount > MAX_CHARS ? "text-red-600" : "text-[#9A9A98]"}`}
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              {charCount}/{MAX_CHARS}
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2D302A] text-white py-3.5 rounded-md text-xs uppercase tracking-[0.25em] hover:bg-[#1A1C18] transition-colors disabled:opacity-60"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="story-submit-button"
          >
            {editing
              ? submitting ? "Saving..." : "Save changes"
              : submitting ? "Sharing..." : "Share your story"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StoryComposer;
